import {useCallback, useMemo} from 'react';
import {NodeLocationInfo} from 'Contexts/NodeCallBackContext';
import {type EnrichedAstNode, createUnitConverter} from 'rendition-core';
import {useViewStore} from 'stores/view';
import {RenderFrameDiv} from 'Components/RenderFrameDiv';
import {useViewContext} from 'Contexts/ViewContext';

interface Props {
    workingNode: EnrichedAstNode;
    locationCallBack: () => NodeLocationInfo;
    scale: React.MutableRefObject<number>;
    horizontalOnly?: boolean;
    verticalOnly?: boolean;
}

export const PaddingHighlighter: React.FC<React.PropsWithChildren<Props>> = ({
    workingNode,
    locationCallBack,
    scale,
    horizontalOnly,
    verticalOnly,
}) => {
    const {view} = useViewContext();
    const previewWidth = useViewStore.use.previewWidth();

    const unitConverter = useMemo(
        () => createUnitConverter({view, nodeId: workingNode.internalId, previewWidth, assignNewValueToNode: false}),
        [view, workingNode.internalId, previewWidth]
    );

    const topPadding = useMemo(
        () =>
            +unitConverter({
                key: 'paddingTop',
                toUnit: 'px',
            }),
        [unitConverter]
    );
    const bottomPadding = useMemo(
        () =>
            +unitConverter({
                key: 'paddingBottom',
                toUnit: 'px',
            }),
        [unitConverter]
    );
    const leftPadding = useMemo(
        () =>
            +unitConverter({
                key: 'paddingLeft',
                toUnit: 'px',
            }),
        [unitConverter]
    );
    const rightPadding = useMemo(
        () =>
            +unitConverter({
                key: 'paddingRight',
                toUnit: 'px',
            }),
        [unitConverter]
    );

    const top = useCallback(() => {
        const location = locationCallBack();
        return {height: topPadding * scale.current + 'px', width: location.actual.width + 'px'};
    }, [topPadding, scale, locationCallBack]);

    const bottom = useCallback(() => {
        const location = locationCallBack();
        return {height: bottomPadding * scale.current + 'px', width: location.actual.width + 'px'};
    }, [bottomPadding, scale, locationCallBack]);

    const left = useCallback(() => {
        const location = locationCallBack();
        return {
            width: leftPadding * scale.current + 'px',
            height: location.actual.height - topPadding * scale.current - bottomPadding * scale.current + 'px',
            top: topPadding * scale.current + 'px',
        };
    }, [leftPadding, scale, locationCallBack, topPadding, bottomPadding]);

    const right = useCallback(() => {
        const location = locationCallBack();
        return {
            width: rightPadding * scale.current + 'px',
            height: location.actual.height - topPadding * scale.current - bottomPadding * scale.current + 'px',
            top: topPadding * scale.current + 'px',
        };
    }, [rightPadding, scale, locationCallBack, topPadding, bottomPadding]);

    return (
        <>
            {!horizontalOnly && (
                <>
                    <RenderFrameDiv
                        onRenderFrame={top}
                        className="absolute bg-purple-400 opacity-20"
                        style={{
                            top: 0,
                            left: 0,
                        }}
                    />
                    <RenderFrameDiv
                        onRenderFrame={bottom}
                        className="absolute bg-purple-400 opacity-20"
                        style={{
                            bottom: 0,
                            left: 0,
                        }}
                    />
                </>
            )}
            {!verticalOnly && (
                <>
                    <RenderFrameDiv
                        onRenderFrame={left}
                        className="absolute bg-purple-400 opacity-20"
                        style={{
                            left: 0,
                        }}
                    />
                    <RenderFrameDiv
                        onRenderFrame={right}
                        className="absolute bg-purple-400 opacity-20"
                        style={{
                            right: 0,
                        }}
                    />
                </>
            )}
        </>
    );
};
